import {useQuery} from "@tanstack/react-query";
import {Button, Modal, Paper, TextInput} from "@mantine/core";
import { CheckCircle as IconCircleCheck, XCircle as IconCircleX, MinusCircle as IconCircleMinus, Crown as IconCrown, MagnifyingGlass as IconSearch, Users as IconUsers } from "@phosphor-icons/react";
import {DataTable} from "mantine-datatable";
import {useCallback, useMemo, useState} from "react";
import {Row, Col} from "../../components/Flex/Flex";
import LoaderScreen from "../../components/Loader/LoaderScreen";
import Page from "../../components/Page/Page";
import CGT from "../../utils/CGT";
import api from "../../utils/api";

const fetchUsers = async () => {
    const {data} = await api.get("/admin/users");
    return data;
}

const formatTimestampRow = (row, config) => {
    if(row[config.field] > 0) {
        let m = CGT.fromSeconds(row[config.field]);
        return <span title={m.toLocaleString(CGT.DATETIME_SHORT)}>{m.toRelative()}</span>;
    } else {
        return <i>Never</i>;
    }
}

const PAGE_SIZE = 50;

export default function UsersPage() {
    const {data: users, isPending} = useQuery({
        queryKey: ['users-list'],
        queryFn: fetchUsers,
    });

    const [globalFilter, setGlobalFilter] = useState('');
    const [page, setPage] = useState(1);

    const [factionDetails, setFactionDetails] = useState(null);

    const filteredUsers = useMemo(() => {
        if (!users) return [];
        const q = globalFilter.trim().toLowerCase();
        if (q.length === 0) return users;
        return users.filter(u =>
            [String(u.handle ?? ''), String(u.id ?? '')].some(v => v.toLowerCase().includes(q))
        );
    }, [users, globalFilter]);

    const paginatedUsers = useMemo(() => {
        return filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    }, [filteredUsers, page]);

    if(isPending || !users) {
        return <LoaderScreen message="Loading users..." />
    }

    const tokenScopesBody = (row) => {
        const scopes = row.token?.scopes || [];
        if(scopes.length === 0) return <i>None</i>;
        return <div className="text-sm">{scopes.map(s => <div key={s}>{s}</div>)}</div>
    };

    const factionsCountBody = (row) => {
        const count = Array.isArray(row.factions) ? row.factions.length : 0;
        return <Button variant="subtle" onClick={() => setFactionDetails(row)}>{count}</Button>
    };


    return (
        <Page>
            <title>Admin - Users</title>
            <Paper p="md">
                <Row>
                    <Col grow={1}>
                        <h2 className="m-0">Users</h2>
                    </Col>
                    <Col>
                        <TextInput
                            leftSection={<IconSearch size={16}/>}
                            value={globalFilter}
                            onChange={e => { setGlobalFilter(e.target.value); setPage(1); }}
                            placeholder="Search"
                            style={{marginBottom: '0.5rem'}}
                        />
                    </Col>
                </Row>

                <DataTable
                    records={paginatedUsers}
                    totalRecords={filteredUsers.length}
                    recordsPerPage={PAGE_SIZE}
                    page={page}
                    onPageChange={setPage}
                    columns={[
                        {accessor: 'id', title: 'UID', cellsClassName: 'text-xs'},
                        {accessor: 'handle', title: 'Name'},
                        {accessor: 'subLevel', title: 'Subscribed'},
                        {accessor: 'subExpire', title: 'Expiration', render: (row) => formatTimestampRow(row, {field: 'subExpire'})},
                        {accessor: 'tokenScopes', title: 'Token Scopes', render: (row) => tokenScopesBody(row)},
                        {accessor: 'lastLogin', title: 'Last Login', render: (row) => formatTimestampRow(row, {field: 'lastLogin'})},
                        {accessor: 'factions', title: '# Factions', render: (row) => factionsCountBody(row), width: 120},
                    ]}
                />
            </Paper>
            {factionDetails ? <FactionDetailsModal user={factionDetails} close={() => setFactionDetails(null)} /> : null}
        </Page>
    )
}


const FactionDetailsModal  = ({user, close}) => {
    const {data: factions} = useQuery({
        queryKey: ['data-factions'],
        queryFn: async () => (await api.get('/admin/factions')).data,
        staleTime: 1000 * 60 * 10,
    });

    const facRows = useMemo(() => {
        if (!user || !Array.isArray(factions)) return [];
        const byId = new Map(factions.map(f => [f.id || f._id, f]));
        const uid = user.id || user.uid;


        return (user.factions || []).map(fid => {
            const f = byId.get(fid);
            if (!f) return {id: fid, name: fid, hasAdditional: false};
            // Determine if user has any additional permissions in this faction

            const permissions = f.permissions.find(p => p.user === uid);

            return {
                ...f,
                permissions,
                isLeader: f.leader?.id === uid,
                isSecond: f.second?.id === uid,
            };
        }).sort((a, b) => a.name.localeCompare(b.name));
    }, [user, factions]);

    const facLogoBody = (row) => row.logo ? <img src={row.logo} alt="logo" width={30} height={30} style={{objectFit: 'contain'}}/> : null;

    const bodyPermission = useCallback((row, field) => {
        if(row.isLeader) {
            return <IconCrown size={16} className="text-blue-500" title="Leader" />
        }
        if(row.isSecond) {
            return <IconUsers size={16} className="text-blue-500" title="Second" />
        }
        if(row.permissions) {
            if(row.permissions[field.field]) {
                return <IconCircleCheck size={16} className="text-green" title="Yes" />
            } else {
                return <IconCircleX size={16} className="text-yellow" title="No" />
            }
        }
        return <IconCircleMinus size={16} className="text-300" title="" />
    }, []);

    return (
        <Modal opened={true} onClose={close} title={user ? `Factions for ${user.handle}` : 'Factions'} size="40rem">
            {user && (
                <DataTable
                    records={facRows}
                    noRecordsText="No factions"
                    columns={[
                        {accessor: '_index', title: '#', render: (_, index) => index + 1, width: 60},
                        {accessor: 'logo', title: 'Logo', render: (row) => facLogoBody(row), width: 70},
                        {accessor: 'name', title: 'Faction'},
                        {accessor: 'manageTavernNetwork', title: 'Manage Taverns', render: (row) => bodyPermission(row, {field: 'manageTavernNetwork'})},
                        {accessor: 'manageLicenses', title: 'Manage Licenses', render: (row) => bodyPermission(row, {field: 'manageLicenses'})},
                        {accessor: 'managePermissions', title: 'Manage Permissions', render: (row) => bodyPermission(row, {field: 'managePermissions'})},
                    ]}
                />
            )}
        </Modal>
    )
}
